import React from 'react';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface HourData {
  hour: number;
  date: string;
  spend: number;
  revenue: number;
  clicks: number;
  searches: number;
  roas: number;
}

interface DayBreakdownChartProps {
  hours: HourData[];
  height?: number;
}

/**
 * DayBreakdownChart - Beautiful line chart using Chart.js showing hourly performance
 * Displays ROAS, RPC, and Spend as lines across 24 hours
 */
export const DayBreakdownChart: React.FC<DayBreakdownChartProps> = ({ hours, height = 280 }) => {
  // Create 24-hour array, filling missing hours with zero data
  const complete24Hours = Array.from({ length: 24 }, (_, hour) => {
    const hourData = hours.find(h => h.hour === hour);
    return hourData || {
      hour,
      spend: 0,
      revenue: 0,
      clicks: 0,
      searches: 0,
      roas: 0,
      date: hours[0]?.date || ''
    };
  });

  // Calculate statistics for ROAS and RPC
  const roasValues = complete24Hours.map(h => h.roas * 100);
  const rpcValues = complete24Hours.map(h => h.clicks > 0 ? h.revenue / h.clicks : 0);

  const roasMin = Math.min(...roasValues);
  const roasMax = Math.max(...roasValues);
  const roasAvg = roasValues.reduce((sum, v) => sum + v, 0) / roasValues.length;

  const rpcMin = Math.min(...rpcValues);
  const rpcMax = Math.max(...rpcValues);
  const rpcAvg = rpcValues.reduce((sum, v) => sum + v, 0) / rpcValues.length;

  // Prepare chart data
  const chartData = {
    labels: complete24Hours.map(h => `${h.hour}h`),
    datasets: [
      {
        label: 'ROAS (%)',
        data: complete24Hours.map(h => h.roas * 100), // Convert to percentage
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        tension: 0, // Sharp lines, not rounded
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        yAxisID: 'y',
        borderDash: [], // Solid line
      },
      {
        label: 'RPC ($)',
        data: complete24Hours.map(h => h.clicks > 0 ? h.revenue / h.clicks : 0),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        tension: 0, // Sharp lines, not rounded
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        yAxisID: 'y1',
        borderDash: [3, 2], // Dashed line for RPC
      },
      {
        label: 'Spend ($)',
        data: complete24Hours.map(h => h.spend),
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.1)',
        borderWidth: 2,
        tension: 0, // Sharp lines, not rounded
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#f97316',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        yAxisID: 'y1',
        borderDash: [5, 3], // Dashed line for Spend
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // We'll use custom legend below
      },
      annotation: {
        annotations: {
          line100: {
            type: 'line' as const,
            yMin: 100,
            yMax: 100,
            yScaleID: 'y',
            borderColor: '#ef4444',
            borderWidth: 1.5,
            borderDash: [4, 4],
            label: {
              display: true,
              content: '100%',
              position: 'end' as const,
              backgroundColor: 'transparent',
              color: '#ef4444',
              font: {
                size: 10,
                weight: 'bold' as const,
              },
              yAdjust: -10,
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
        intersect: false,
        mode: 'index' as const,
        position: 'nearest' as const,
        titleFont: {
          size: 12,
          weight: 'bold' as const,
        },
        bodyFont: {
          size: 11,
        },
        padding: {
          top: 10,
          right: 12,
          bottom: 10,
          left: 12,
        },
        caretSize: 6,
        caretPadding: 6,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.datasetIndex === 0) {
                // ROAS
                label += context.parsed.y.toFixed(1) + '%';
              } else {
                // RPC or Spend
                label += '$' + context.parsed.y.toFixed(2);
              }
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 9,
          },
          color: '#6b7280',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: false, // Hide default title
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.06)',
          lineWidth: 1,
        },
        ticks: {
          display: false, // Hide default ticks - we use custom labels
        },
        border: {
          display: false,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: false, // Hide default title
        },
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hide default ticks - we use custom labels
        },
        border: {
          display: false,
        },
      },
    },
  };

  // Calculate label positions - we'll show 3 evenly distributed labels including average
  // Chart rendering area calculation
  const topPadding = 35; // px - increased for X button clearance
  const bottomPadding = 50; // px for legend
  const leftPadding = 60; // px for ROAS labels
  const rightPadding = 70; // px for RPC labels
  const chartHeight = height - topPadding - bottomPadding;

  // Chart.js internal canvas positioning (empirically determined)
  const chartCanvasTop = topPadding + 12; // Chart.js adds internal padding
  const chartCanvasHeight = chartHeight - 48; // Chart.js reserves space for x-axis labels and padding

  // Helper function to calculate pixel position for a value on the axis
  const calculatePosition = (value: number, min: number, max: number): number => {
    // For a Y axis, position 0 is at top, so we need to invert
    // value at max should be at top (0px offset from chartCanvasTop)
    // value at min should be at bottom (chartCanvasHeight offset from chartCanvasTop)
    const ratio = (max - value) / (max - min);
    return chartCanvasTop + ratio * chartCanvasHeight;
  };

  // Create labels with calculated positions for ROAS
  const roasLabels = [
    { value: roasMax, label: `${roasMax.toFixed(0)}%`, isAvg: false, top: calculatePosition(roasMax, roasMin, roasMax) },
    { value: roasAvg, label: `Avg: ${roasAvg.toFixed(0)}%`, isAvg: true, top: calculatePosition(roasAvg, roasMin, roasMax) },
    { value: roasMin, label: `${roasMin.toFixed(0)}%`, isAvg: false, top: calculatePosition(roasMin, roasMin, roasMax) },
  ];

  // Create labels with calculated positions for RPC
  const rpcLabels = [
    { value: rpcMax, label: `$${rpcMax.toFixed(2)}`, isAvg: false, top: calculatePosition(rpcMax, rpcMin, rpcMax) },
    { value: rpcAvg, label: `Avg: $${rpcAvg.toFixed(2)}`, isAvg: true, top: calculatePosition(rpcAvg, rpcMin, rpcMax) },
    { value: rpcMin, label: `$${rpcMin.toFixed(2)}`, isAvg: false, top: calculatePosition(rpcMin, rpcMin, rpcMax) },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: height,
        bgcolor: 'transparent',
        p: 0,
        position: 'relative',
      }}
    >
      {/* Chart container with proper padding */}
      <Box sx={{
        height: height - bottomPadding,
        position: 'relative',
        pt: `${topPadding}px`,
        pb: 0,
        pl: `${leftPadding}px`,
        pr: `${rightPadding}px`
      }}>
        <Line data={chartData} options={options} />
      </Box>

      {/* Left axis labels - ROAS - absolutely positioned based on value */}
      {roasLabels.map((item, idx) => (
        <Typography
          key={idx}
          sx={{
            position: 'absolute',
            left: 8,
            top: `${item.top}px`,
            fontSize: item.isAvg ? '9px' : '10px',
            color: '#3b82f6',
            fontWeight: item.isAvg ? 700 : 600,
            lineHeight: 1,
            zIndex: 1,
            pointerEvents: 'none',
            transform: 'translateY(-50%)', // Center the text on the axis line
            ...(item.isAvg && {
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              border: '1px solid #3b82f6',
            })
          }}
        >
          {item.label}
        </Typography>
      ))}

      {/* Right axis labels - RPC - absolutely positioned based on value */}
      {rpcLabels.map((item, idx) => (
        <Typography
          key={idx}
          sx={{
            position: 'absolute',
            right: 8,
            top: `${item.top}px`,
            fontSize: item.isAvg ? '9px' : '10px',
            color: '#10b981',
            fontWeight: item.isAvg ? 700 : 600,
            textAlign: 'right',
            lineHeight: 1,
            zIndex: 1,
            pointerEvents: 'none',
            transform: 'translateY(-50%)', // Center the text on the axis line
            ...(item.isAvg && {
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              border: '1px solid #10b981',
            })
          }}
        >
          {item.label}
        </Typography>
      ))}

      {/* Custom Legend - positioned at bottom inside the container */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 0,
          right: 0,
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 2.5,
              bgcolor: '#3b82f6',
              borderRadius: 0.5
            }}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
            ROAS
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 2.5,
              bgcolor: '#10b981',
              borderRadius: 0.5
            }}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
            RPC
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 2.5,
              bgcolor: '#f97316',
              borderRadius: 0.5
            }}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
            Spend
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 1.5,
              border: '1.5px dashed #ef4444',
              borderRadius: 0
            }}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
            100%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DayBreakdownChart;
